import { useMemo } from 'react'
import { gql } from '@apollo/client'
import { useTranslations } from '@npco/utils-translations'
import {
  Flex,
  NumberBadge,
  NumberBadgeVariant,
  Toggle,
} from '@npco/zeller-design-system'
import { useGoToCCOutstandingExpenseComingSoon } from 'features/CCOutstandingExpenseComingSoon/cc-outstanding-expense-coming-soon-routing'
import { useFeatureFlags } from 'features/FeatureFlags'

import { useIsMobileResolution } from 'hooks/useIsMobileResolution'

import { useOutstandingFilterContext } from '../context/useOutstandingFilterContext'
import { OutstandingExpensesToggleDebitCardV2Fragment } from './OutstandingExpensesToggle.generated'
import { outstandingExpensesToggleTranslations } from './OutstandingExpensesToggle.i18n'

interface OutstandingExpensesToggleProps {
  card: OutstandingExpensesToggleDebitCardV2Fragment | null
  isFilterOn: boolean
  toggleFilter: () => void
}

export const OutstandingExpensesToggle = ({
  card,
  isFilterOn,
  toggleFilter,
}: OutstandingExpensesToggleProps) => {
  const isMobile = useIsMobileResolution()
  const flags = useFeatureFlags()
  const t = useTranslations(outstandingExpensesToggleTranslations)
  const { goToCCOutstandingExpenseComingSoon } =
    useGoToCCOutstandingExpenseComingSoon()
  const {
    isOutstandingFilterSettingsAllInactive,
    isLoadingOutstandingFilterSettings,
  } = useOutstandingFilterContext()

  const outstandingTransactionsCount = card?.outstandingTransactions ?? 0

  const isFilterToggleDisabled = useMemo(() => {
    if (!flags.CorpCardsOutstandingTransactionsFilter) {
      return true
    }

    if (
      flags.CorpCardsSettings &&
      (isOutstandingFilterSettingsAllInactive ||
        isLoadingOutstandingFilterSettings)
    ) {
      return true
    }

    return false
  }, [
    flags.CorpCardsOutstandingTransactionsFilter,
    flags.CorpCardsSettings,
    isOutstandingFilterSettingsAllInactive,
    isLoadingOutstandingFilterSettings,
  ])

  const toggleText = useMemo(() => {
    const text = isMobile ? t('badgeTextMobile') : t('badgeText')

    if (isFilterToggleDisabled) {
      return text
    }

    if (
      flags.CorpCardsOutstandingTransactionsFilter &&
      flags.CorpCardsSettings &&
      !isFilterOn
    ) {
      return `${outstandingTransactionsCount} ${text}`
    }

    return text
  }, [
    t,
    isMobile,
    isFilterToggleDisabled,
    isFilterOn,
    outstandingTransactionsCount,
    flags.CorpCardsSettings,
    flags.CorpCardsOutstandingTransactionsFilter,
  ])

  const toggleTextVariant = useMemo(() => {
    if (
      flags.CorpCardsOutstandingTransactionsFilter &&
      flags.CorpCardsSettings &&
      !isOutstandingFilterSettingsAllInactive &&
      outstandingTransactionsCount > 0
    ) {
      return NumberBadgeVariant.SoftAlert
    }

    return NumberBadgeVariant.Default
  }, [
    flags.CorpCardsSettings,
    flags.CorpCardsOutstandingTransactionsFilter,
    outstandingTransactionsCount,
    isOutstandingFilterSettingsAllInactive,
  ])

  return (
    <Flex
      gap="10px"
      position="absolute"
      zIndex="10"
      right="0"
      top="0"
      alignItems="center"
      onClick={() => {
        if (!flags.CorpCardsOutstandingTransactionsFilter) {
          goToCCOutstandingExpenseComingSoon({ stage: 'coming-soon-modal' })
        }
      }}
    >
      <NumberBadge variant={toggleTextVariant} text={toggleText} />
      <Toggle
        checked={isFilterOn}
        disabled={isFilterToggleDisabled}
        onChange={toggleFilter}
      />
    </Flex>
  )
}

OutstandingExpensesToggle.fragments = {
  DebitCardV2: gql`
    fragment OutstandingExpensesToggleDebitCardV2Fragment on DebitCardV2 {
      outstandingTransactions
    }
  `,
}
